import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Instagram, Facebook, Youtube, Save, ExternalLink } from "lucide-react";
import TikTokIcon from "@/components/icons/TikTokIcon";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const EMPTY = { instagram_url: "", tiktok_url: "", facebook_url: "", youtube_url: "" };

export default function SocialAdmin() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/social").then((r) => setForm({ ...EMPTY, ...r.data }));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/social", form);
      toast.success(t("admin.social.toastSaved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="social-admin" className="space-y-8 max-w-3xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-bordeaux-accent mb-2">{t("admin.social.eyebrow")}</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t("admin.social.title")}</h1>
          <p className="text-slate-600 mt-2">{t("admin.social.subtitle")}</p>
        </div>
        <Link
          to="/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-bordeaux border border-slate-300 hover:border-bordeaux rounded-sm px-3 py-2"
        >
          <ExternalLink size={14} /> {t("admin.social.viewPublic")}
        </Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8 space-y-5">
        <SocialField
          icon={<Instagram size={16} className="text-[#E1306C]" />}
          label="Instagram"
          value={form.instagram_url}
          onChange={(v) => setForm({ ...form, instagram_url: v })}
          placeholder="https://instagram.com/your-handle"
          testId="social-instagram-input"
        />
        <SocialField
          icon={<TikTokIcon size={16} className="text-slate-900" />}
          label="TikTok"
          value={form.tiktok_url}
          onChange={(v) => setForm({ ...form, tiktok_url: v })}
          placeholder="https://tiktok.com/@your-handle"
          testId="social-tiktok-input"
        />
        <SocialField
          icon={<Facebook size={16} className="text-[#1877F2]" />}
          label="Facebook"
          value={form.facebook_url}
          onChange={(v) => setForm({ ...form, facebook_url: v })}
          placeholder="https://facebook.com/your-page"
          testId="social-facebook-input"
        />
        <SocialField
          icon={<Youtube size={16} className="text-[#FF0000]" />}
          label="YouTube"
          value={form.youtube_url}
          onChange={(v) => setForm({ ...form, youtube_url: v })}
          placeholder="https://youtube.com/@your-channel"
          testId="social-youtube-input"
        />

        <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
          {t("admin.social.hint")}
        </p>

        <div>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="social-save-btn"
            className="bg-bordeaux hover:bg-bordeaux-dark text-beige-light rounded-sm"
          >
            <Save size={14} className="mr-2" /> {busy ? t("admin.common.saving") : t("admin.common.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SocialField({ icon, label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
        {icon} {label}
      </Label>
      <Input
        type="url"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="rounded-sm border-slate-300"
      />
    </div>
  );
}
